// My Hours — personal time clock summary for the active PIN user.
// Pay-period view with totals + entry list. No editing — entries go to
// manager review when clocked out.
// Sep 8 mobile rebuild: period as chips, three stats, entries as cards
// (one per shift) instead of a five-column table.

import { useEffect, useState } from 'react';
import { api } from '../../api';
import PinGate from '../../components/PinGate';
import { useIsMobile, MobilePage } from '../../mobile';

function fmtHours(seconds) {
  if (!seconds && seconds !== 0) return '—';
  return (seconds / 3600).toFixed(2) + 'h';
}
function toDate(s) { return s ? new Date(s + (s.endsWith('Z') ? '' : 'Z')) : null; }
function fmtDay(s, weekday = true) { const d = toDate(s); return d ? d.toLocaleDateString(undefined, { ...(weekday ? { weekday: 'short' } : {}), month: 'short', day: 'numeric' }) : '—'; }
function fmtTime(s) { const d = toDate(s); return d ? d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) : '—'; }
function statusLabel(s) {
  return { open: 'On clock', pending_review: 'Pending review', approved: 'Approved', rejected: 'Rejected' }[s] || s;
}
const STATUS = {
  open: { bg: '#eff6ff', fg: '#2563eb' }, pending_review: { bg: '#fffbeb', fg: '#b45309' },
  approved: { bg: '#f0fdf4', fg: '#15803d' }, rejected: { bg: '#fef2f2', fg: '#dc2626' },
};

function MyHoursContent({ activeUser }) {
  const isMobile = useIsMobile();
  const [period, setPeriod] = useState('current');
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.myHours(period).then(setData).catch(e => setErr(e.message));
  }, [period, activeUser]);

  const entries = data?.entries || [];
  const body = (
    <>
      <div className="m-chips">
        <button type="button" className={`m-chip ${period === 'current' ? 'active' : ''}`} onClick={() => setPeriod('current')}>This period</button>
        <button type="button" className={`m-chip ${period === 'previous' ? 'active' : ''}`} onClick={() => setPeriod('previous')}>Last period</button>
      </div>
      {err && <div className="alert alert-error">{err}</div>}
      {data?.period && (
        <div className="m-stats">
          <div className="m-stat" style={{ '--m-stat-color': '#3b82f6', flex: 1.4 }}>
            <div className="m-stat-value" style={{ fontSize: 15 }}>{fmtDay(data.period.start_date + 'T12:00:00', false)} – {fmtDay(data.period.end_date + 'T12:00:00', false)}</div>
            <div className="m-stat-label">Period</div>
          </div>
          <div className="m-stat" style={{ '--m-stat-color': '#16a34a' }}><div className="m-stat-value">{fmtHours(data.total_seconds)}</div><div className="m-stat-label">Total</div></div>
          <div className="m-stat" style={{ '--m-stat-color': '#64748b' }}><div className="m-stat-value" style={{ fontSize: 15, textTransform: 'capitalize' }}>{data.period.status}</div><div className="m-stat-label">Status</div></div>
        </div>
      )}
      <div className="m-section">Shifts{entries.length ? ` · ${entries.length}` : ''}</div>
      {data && entries.length === 0 ? (
        <div className="m-card" style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 24 }}>No entries in this period.</div>
      ) : (
        <div className="m-list">
          {entries.map(e => {
            const s = STATUS[e.status] || { bg: 'var(--bg-inset)', fg: 'var(--text-secondary)' };
            return (
              <div key={e.id} className="m-card" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <div style={{ fontSize: 15, fontWeight: 700 }}>{fmtDay(e.clock_in_at)}</div>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{fmtTime(e.clock_in_at)} → {e.clock_out_at ? fmtTime(e.clock_out_at) : 'still on'}</div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span className="m-pill" style={{ background: s.bg, color: s.fg }}>{statusLabel(e.status).toUpperCase()}</span>
                    {(e.manager_notes || e.notes) && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{e.manager_notes || e.notes}</span>}
                  </div>
                </div>
                <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' }}>{fmtHours(e.adjusted_total_seconds ?? e.total_seconds)}</div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );

  if (isMobile) return <MobilePage title="My Hours" subtitle={activeUser?.full_name}>{body}</MobilePage>;
  return (
    <div className="page" style={{ maxWidth: 720 }}>
      <div className="page-header"><h2>My Hours</h2></div>
      {body}
    </div>
  );
}

export default function MyHoursPage() {
  return (
    <PinGate title="My Hours">
      {(activeUser) => <MyHoursContent activeUser={activeUser} />}
    </PinGate>
  );
}
